import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaOnboardingProgressV1Service, serviceName } from '../service';
import { GetLatestOnboardingProgressByLocationIDIO, ServiceQueries } from '../types';
import { useOnboardingProgressV1Queries } from './use-onboarding-progress-v1-queries';
import {
  UseOnboardingProgressV1QueryEndpointArgs,
  useOnboardingProgressV1Query,
} from './use-onboarding-progress-v1-query';

/**
 * A hook that returns a query for the `GetLatestOnboardingProgressByLocationID` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetLatestOnboardingProgressByLocationIDQuery = <
  E = unknown,
  D = GetLatestOnboardingProgressByLocationIDIO['output']
>(
  args: UseOnboardingProgressV1QueryEndpointArgs<'GetLatestOnboardingProgressByLocationID', E, D>
) =>
  useOnboardingProgressV1Query({
    endpointName: 'GetLatestOnboardingProgressByLocationID',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetLatestOnboardingProgressByLocationID` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetLatestOnboardingProgressByLocationIDQueries = <
  E = unknown,
  D = GetLatestOnboardingProgressByLocationIDIO['output']
>(
  argsArr: UseOnboardingProgressV1QueryEndpointArgs<'GetLatestOnboardingProgressByLocationID', E, D>[]
) =>
  useOnboardingProgressV1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetLatestOnboardingProgressByLocationID',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetLatestOnboardingProgressByLocationID` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetLatestOnboardingProgressByLocationID = () => {
  const queryClient = useQueryClient();

  const fetchGetLatestOnboardingProgressByLocationID = async <
    E = unknown,
    D = GetLatestOnboardingProgressByLocationIDIO['output']
  >({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseOnboardingProgressV1QueryEndpointArgs<'GetLatestOnboardingProgressByLocationID', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetLatestOnboardingProgressByLocationIDIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetLatestOnboardingProgressByLocationID'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetLatestOnboardingProgressByLocationIDIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetLatestOnboardingProgressByLocationID'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetLatestOnboardingProgressByLocationID'>({
          serviceName,
          endpointName: 'GetLatestOnboardingProgressByLocationID',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaOnboardingProgressV1Service.GetLatestOnboardingProgressByLocationID(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetLatestOnboardingProgressByLocationID;
};
